/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : useAutoRefresh
 *   Feature      : 226-universal-page-affordances
 *   Description  : Silent, visibility-aware auto-refresh for dashboard/insight
 *                  pages. Fires the page's refresh every `intervalMs` (default
 *                  5 min), PAUSES while the tab is hidden and resumes + catches
 *                  up immediately on focus, and BACKS OFF exponentially after
 *                  consecutive failures (fail-open — never hammers a failing
 *                  API), resetting on success or focus. No UI of its own; the
 *                  header freshness indicator reflects it (Spec 226 US3/FR-005).
 *   Constitution : v1.23.0
 *   Author       : Peter Dobler
 *   Created      : 2026-07-07
 * ============================================================================
 */
import { useEffect, useRef } from 'react';
import { AUTO_REFRESH_INTERVAL_MS } from '../config/autoRefresh';

export interface UseAutoRefreshOptions {
  /** Base cadence in ms (default 5 minutes). */
  intervalMs?: number;
  /** Master switch (default true). */
  enabled?: boolean;
  /** Consecutive failures before backoff kicks in (default 1). */
  failuresBeforeBackoff?: number;
  /** Cap on the backed-off interval (default 30 min). */
  maxIntervalMs?: number;
  /**
   * The page's last successful fetch (usePageData's `lastFetchedAt`). When it moves for any
   * reason other than this timer, such as a manual Refresh, the interval restarts from now
   * and the backoff clears.
   */
  lastFetchedAt?: number;
}

/**
 * Drive `refresh` on a silent, visibility-gated timer with failure backoff.
 * `refresh` must reject when the re-fetch fails (usePageData's does), or the backoff never runs.
 */
export function useAutoRefresh(
  refresh: () => void | Promise<void>,
  options: UseAutoRefreshOptions = {}
): void {
  const {
    intervalMs = AUTO_REFRESH_INTERVAL_MS,
    enabled = true,
    failuresBeforeBackoff = 1,
    maxIntervalMs = 30 * 60 * 1000,
    lastFetchedAt,
  } = options;

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const failuresRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set by the effect below: clears the backoff and starts the interval again from now.
  const restartRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!enabled) return;

    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    // Current delay with exponential backoff once past the failure threshold.
    const nextDelay = () => {
      const over = failuresRef.current - failuresBeforeBackoff;
      if (over < 0) return intervalMs;
      return Math.min(intervalMs * 2 ** (over + 1), maxIntervalMs);
    };

    const tick = async () => {
      if (document.hidden) {
        // Paused while hidden; the visibility handler resumes + catches up.
        return;
      }
      try {
        await refreshRef.current();
        failuresRef.current = 0;
      } catch {
        failuresRef.current += 1;
      } finally {
        schedule();
      }
    };

    const schedule = () => {
      clear();
      timerRef.current = setTimeout(tick, nextDelay());
    };

    // Only visibilitychange: a tab return also fires window focus, and listening to both
    // refreshed twice. Coming back clears the backoff, then catches up at once.
    const onVisibility = () => {
      if (!document.hidden) {
        failuresRef.current = 0;
        void tick();
      }
    };

    restartRef.current = () => {
      failuresRef.current = 0;
      schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clear();
      restartRef.current = () => undefined;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs, failuresBeforeBackoff, maxIntervalMs]);

  // A fetch outside the timer (a manual Refresh) restarts the interval. The first value is
  // the page's initial load, which the effect above has just scheduled from.
  const seenFetchRef = useRef(lastFetchedAt);
  useEffect(() => {
    if (lastFetchedAt === seenFetchRef.current) return;
    seenFetchRef.current = lastFetchedAt;
    restartRef.current();
  }, [lastFetchedAt]);
}

export default useAutoRefresh;
